/**
 * WidgetConfigPanel Component
 *
 * Dispatcher for widget configuration panels.
 * Routes to the appropriate config panel based on widget type.
 */

import React, { useCallback } from 'react';
import { WidgetConfigPanelProps } from '../Widgets/types';
import {
  BUTTON_DEFAULT_CONFIG,
  YOUR_BUILD_DEFAULT_CONFIG,
  ButtonWidgetConfig,
  YourBuildWidgetConfig,
} from '../../config/widgetDefaults';
import { useUIModelState } from '../../contexts/UIModelStateContext';
import ButtonConfigPanel from './ButtonConfigPanel';
import YourBuildConfigPanel from './YourBuildConfigPanel';

export const WidgetConfigPanel = ({
  widget,
  onConfigChange,
}: WidgetConfigPanelProps) => {
  const { state } = useUIModelState();

  // Get embedded configuration
  const embeddedConfig = widget.configuration || {};

  // Type-safe change handlers for each widget type
  const handleButtonChange = useCallback(
    (config: ButtonWidgetConfig) => {
      onConfigChange(config as unknown as Record<string, unknown>);
    },
    [onConfigChange]
  );

  const handleYourBuildChange = useCallback(
    (config: YourBuildWidgetConfig) => {
      onConfigChange(config as unknown as Record<string, unknown>);
    },
    [onConfigChange]
  );

  // Render config panel based on widget type
  switch (widget.widgetType) {
    case 'divider':
    case 'contentBlock':
      return (
        <div className="tw-space-y-4">
          <h3 className="tw-text-sm tw-font-semibold tw-text-ink">
            {widget.widgetType === 'divider' ? 'Divider Widget' : 'Content Block Widget'}
          </h3>
          <div className="tw-p-3 tw-bg-surface-sunken tw-border tw-border-edge tw-rounded">
            <p className="tw-text-sm tw-text-ink-secondary">
              {widget.widgetType === 'divider'
                ? 'The Divider widget displays a visual separator between content sections.'
                : 'The Content Block widget displays static content.'}
            </p>
            <p className="tw-text-xs tw-text-ink-tertiary tw-mt-2">
              No additional configuration required.
            </p>
          </div>
        </div>
      );

    case 'button':
      return (
        <ButtonConfigPanel
          config={{ ...BUTTON_DEFAULT_CONFIG, ...embeddedConfig } as ButtonWidgetConfig}
          onChange={handleButtonChange}
        />
      );

    case 'yourBuild': {
      // Read config from sharedWidgets (not widget.configuration)
      const sharedConfig = state.model?.layout?.sharedWidgets?.yourBuildWidget;
      const resolvedConfig = {
        ...YOUR_BUILD_DEFAULT_CONFIG,
        ...(sharedConfig || {}),
      } as YourBuildWidgetConfig;
      return (
        <YourBuildConfigPanel
          config={resolvedConfig}
          onChange={handleYourBuildChange}
        />
      );
    }

    case 'build':
      return (
        <div className="tw-space-y-4">
          <h3 className="tw-text-sm tw-font-semibold tw-text-ink">
            Build Widget
          </h3>
          <div className="tw-p-3 tw-bg-surface-sunken tw-border tw-border-edge tw-rounded">
            <p className="tw-text-sm tw-text-ink-secondary">
              The Build widget displays the current build summary at runtime.
            </p>
            <p className="tw-text-xs tw-text-ink-tertiary tw-mt-2">
              No additional configuration required.
            </p>
          </div>
        </div>
      );

    default:
      return (
        <div className="tw-space-y-4">
          <h3 className="tw-text-sm tw-font-semibold tw-text-ink">
            Widget: {widget.widgetType}
          </h3>
          <div className="tw-p-3 tw-bg-tone-warning-tint tw-border tw-border-edge tw-rounded">
            <p className="tw-text-sm tw-text-tone-warning-ink">
              Configuration panel for this widget type is not yet available.
            </p>
          </div>
          <div className="tw-p-3 tw-bg-surface-sunken tw-border tw-border-edge tw-rounded">
            <p className="tw-text-xs tw-text-ink-secondary tw-font-mono">
              Widget ID: {widget.id}
            </p>
            <p className="tw-text-xs tw-text-ink-secondary tw-font-mono">
              Type: {widget.widgetType}
            </p>
            <p className="tw-text-xs tw-text-ink-secondary tw-font-mono">
              Reusable: {widget.isReusable ? 'Yes' : 'No'}
            </p>
          </div>
        </div>
      );
  }
};

export default WidgetConfigPanel;
